import { createFileRoute } from '@tanstack/react-router'
import { ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { DataTable, SortableHeader } from '@/components/ui/data-table'
import { useQuery } from '@tanstack/react-query'
import { adminQueries } from '@/services/adminApi'
import { Phone, CheckCircle } from 'lucide-react'

export const Route = createFileRoute('/admin/phone-numbers')({
  component: AdminPhoneNumbersPage,
})

function AdminPhoneNumbersPage() {
  const { data: response, isLoading } = useQuery(adminQueries.phoneNumbers())
  const numbers = response?.phoneNumbers ?? []

  const columns: ColumnDef<typeof numbers[number], any>[] = [
    {
      accessorKey: 'number',
      header: ({ column }) => <SortableHeader column={column} label="Number" />,
      cell: ({ row }) => <span className="font-mono">{row.original.number}</span>,
    },
    {
      accessorKey: 'provider',
      header: ({ column }) => <SortableHeader column={column} label="Provider" />,
      cell: ({ row }) => (
        <Badge variant="outline" className="capitalize">
          {row.original.provider || 'twilio'}
        </Badge>
      ),
    },
    {
      accessorKey: 'type',
      header: ({ column }) => <SortableHeader column={column} label="Type" />,
      cell: ({ row }) => (
        <Badge variant="outline" className="capitalize">
          {row.original.type || 'unknown'}
        </Badge>
      ),
    },
    {
      id: 'capabilities',
      header: 'Capabilities',
      cell: ({ row }) => {
        const caps = row.original.capabilities
        if (!caps?.voice && !caps?.sms && !caps?.mms) {
          return <span className="text-muted-foreground text-xs">-</span>
        }
        return (
          <div className="flex gap-1 flex-wrap">
            {caps.voice && <Badge variant="secondary" className="text-xs">Voice</Badge>}
            {caps.sms && <Badge variant="secondary" className="text-xs">SMS</Badge>}
            {caps.mms && <Badge variant="secondary" className="text-xs">MMS</Badge>}
          </div>
        )
      },
    },
    {
      id: 'callerId',
      header: 'Caller ID',
      cell: ({ row }) => row.original.verifiedCallerId
        ? <CheckCircle className="h-4 w-4 text-green-500" />
        : <span className="text-muted-foreground">-</span>,
    },
    {
      accessorKey: 'purchasedAt',
      header: ({ column }) => <SortableHeader column={column} label="Purchased" />,
      cell: ({ row }) => {
        const d = row.original.purchasedAt
        if (!d) return '-'
        return <span className="text-muted-foreground text-sm">{new Date(d).toLocaleString()}</span>
      },
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Phone Numbers</h1>
        <Badge variant="secondary" className="text-sm">
          {numbers.length} number{numbers.length !== 1 ? 's' : ''}
        </Badge>
      </div>

      <Card flush className="overflow-hidden">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            Owned Numbers
          </CardTitle>
          <CardDescription>
            Phone numbers we own on Twilio, available for use as caller IDs.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : !numbers.length ? (
            <p className="text-sm text-muted-foreground text-center py-8">No phone numbers found.</p>
          ) : (
            <DataTable
              columns={columns}
              data={numbers}
              enableSearch={false}
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
